import * as React from 'react'
import {
  TextB,
  TextItalic,
  TextUnderline,
  TextStrikethrough,
  ListBullets,
  ListNumbers,
  LinkSimple,
  Paperclip,
  Quotes,
  PaperPlaneTilt,
  type Icon,
} from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Button } from './button'
import { InputGroup, InputGroupAddon, InputGroupButton } from './input-group'
import { type FieldStatus } from './field'

/**
 * RichTextEditor — the *chrome* for a rich-text field: a formatting toolbar over
 * an editable surface, built on the `InputGroup` shell (shared border + focus
 * ring), a `block-start` toolbar row, and `InputGroupButton`s. It bundles **no
 * editing engine** — the toolbar buttons emit `onCommand(command)` and the
 * surface is a plain `contentEditable`, so the consuming app wires its own
 * engine (Quill, TipTap, Lexical, …) to those hooks. This keeps the design
 * system free of a heavy editor dependency while owning the look.
 *
 *   <RichTextEditor
 *     placeholder="Write a description…"
 *     activeCommands={['bold']}
 *     onCommand={(cmd) => editor.toggle(cmd)}
 *   />
 *
 * Pass `toolbar` to replace the default button set (compose your own with
 * `RichTextEditorButton` + `RichTextEditorDivider`). Grab the surface via
 * `contentRef` to mount an engine onto it. Set `onSend` / `onDiscard` to render
 * a footer with Send (primary) + Discard (ghost) actions, or pass `footer` for a
 * fully custom action row (e.g. an attachment control beside Send).
 *
 * Placeholder note: it's the CSS `:empty` pseudo on the surface, so to reset the
 * editor and restore the placeholder, clear `innerHTML` — not just the text —
 * since a leftover `<br>` keeps the element from matching `:empty`.
 */

export type RichTextCommand =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikethrough'
  | 'bulletList'
  | 'orderedList'
  | 'link'
  | 'attachment'
  | 'blockquote'
  | 'code'

interface ToolbarItem {
  command: RichTextCommand
  icon: Icon
  label: string
  /** Start of a new visual group — render a divider before it. */
  group?: boolean
}

const DEFAULT_ITEMS: ToolbarItem[] = [
  { command: 'bold', icon: TextB, label: 'Bold' },
  { command: 'italic', icon: TextItalic, label: 'Italic' },
  { command: 'underline', icon: TextUnderline, label: 'Underline' },
  { command: 'strikethrough', icon: TextStrikethrough, label: 'Strikethrough' },
  { command: 'bulletList', icon: ListBullets, label: 'Bulleted list', group: true },
  { command: 'orderedList', icon: ListNumbers, label: 'Numbered list' },
  { command: 'link', icon: LinkSimple, label: 'Link', group: true },
  { command: 'attachment', icon: Paperclip, label: 'Attach file' },
  // No code-block button: CRM notes/emails never contain code, and it just adds
  // toolbar noise. `blockquote` stays — quoting is useful in email replies.
  { command: 'blockquote', icon: Quotes, label: 'Quote', group: true },
]

/** A thin vertical rule separating toolbar groups. */
export const RichTextEditorDivider = () => (
  <span aria-hidden="true" className="mx-1 h-5 w-px self-center bg-fbui-gray-200" />
)
RichTextEditorDivider.displayName = 'RichTextEditorDivider'

export interface RichTextEditorButtonProps
  extends React.ComponentPropsWithoutRef<typeof InputGroupButton> {
  /** Render the button pressed (sets `aria-pressed` + a tinted background). */
  active?: boolean
}

/**
 * A toolbar button — an `InputGroupButton` with a pressed/active state. Use it
 * to build a custom `toolbar`.
 */
export const RichTextEditorButton = React.forwardRef<
  HTMLButtonElement,
  RichTextEditorButtonProps
>(({ active, className, size = 'icon-sm', ...props }, ref) => (
  <InputGroupButton
    ref={ref}
    size={size}
    aria-pressed={active}
    className={cn(active && 'bg-fbui-gray-100 text-fbui-gray-700', className)}
    {...props}
  />
))
RichTextEditorButton.displayName = 'RichTextEditorButton'

export interface RichTextEditorProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Border + ring color. Inherited from a parent `<Field>` when omitted. */
  status?: FieldStatus
  /** Disable the toolbar + make the surface read-only. */
  disabled?: boolean
  /** Placeholder shown while the surface is empty. */
  placeholder?: string
  /** Replace the default toolbar (compose with `RichTextEditorButton`). */
  toolbar?: React.ReactNode
  /** Fired by the default toolbar buttons with the command name. */
  onCommand?: (command: RichTextCommand) => void
  /** Command names to render as pressed on the default toolbar. */
  activeCommands?: RichTextCommand[]
  /** Accessible name for the editable surface. */
  contentLabel?: string
  /** Ref to the `contentEditable` surface (mount an engine here). */
  contentRef?: React.Ref<HTMLDivElement>
  /** Classes for the editable surface (e.g. a taller `min-h`). */
  contentClassName?: string
  /** Fired by the footer Send action. Renders the footer when set. */
  onSend?: () => void
  /** Fired by the footer Discard action. Renders the footer when set. */
  onDiscard?: () => void
  /** Send button label. */
  sendLabel?: string
  /** Discard button label. */
  discardLabel?: string
  /** Disable just the Send action (e.g. while the editor is empty). */
  sendDisabled?: boolean
  /** Replace the default footer actions (e.g. add an attachment control). */
  footer?: React.ReactNode
  /** Content pinned to the footer's left edge (e.g. attachment chips), while
   *  the Send/Discard actions stay right-aligned. Renders the footer when set. */
  footerStart?: React.ReactNode
}

export const RichTextEditor = React.forwardRef<
  HTMLDivElement,
  RichTextEditorProps
>(
  (
    {
      status,
      disabled = false,
      placeholder,
      toolbar,
      onCommand,
      activeCommands,
      contentLabel = 'Rich text editor',
      contentRef,
      contentClassName,
      onSend,
      onDiscard,
      sendLabel = 'Send',
      discardLabel = 'Discard',
      sendDisabled = false,
      footer,
      footerStart,
      className,
      ...props
    },
    ref
  ) => {
    const hasFooter =
      footer != null ||
      footerStart != null ||
      onSend != null ||
      onDiscard != null
    const defaultToolbar = DEFAULT_ITEMS.map(({ command, icon: Glyph, label, group }) => (
      <React.Fragment key={command}>
        {group && <RichTextEditorDivider />}
        <RichTextEditorButton
          aria-label={label}
          active={activeCommands?.includes(command)}
          // Keep focus in the surface so a command applies to the selection.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onCommand?.(command)}
        >
          <Glyph aria-hidden="true" />
        </RichTextEditorButton>
      </React.Fragment>
    ))

    return (
      <InputGroup
        ref={ref}
        status={status}
        disabled={disabled}
        className={className}
        {...props}
      >
        <InputGroupAddon
          align="block-start"
          divided
          role="toolbar"
          aria-label="Text formatting"
          className="flex-wrap gap-0.5 px-1.5 py-1"
        >
          {toolbar ?? defaultToolbar}
        </InputGroupAddon>
        <div
          ref={contentRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={contentLabel}
          aria-disabled={disabled || undefined}
          aria-placeholder={placeholder}
          data-placeholder={placeholder}
          className={cn(
            'fbui-scroll max-h-80 min-h-28 basis-full px-3 py-2 text-sm text-fbui-gray-800 outline-none',
            // Placeholder via the empty pseudo-element (no value to bind on a DOM-owned surface).
            'empty:before:pointer-events-none empty:before:text-fbui-gray-400 empty:before:content-[attr(data-placeholder)]',
            // Rich-content rendering — the app's CSS reset strips list markers,
            // so re-assert them (plus blockquote / code / link styling) on the
            // formatted content the toolbar produces.
            '[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5',
            '[&_blockquote]:my-1 [&_blockquote]:border-l-2 [&_blockquote]:border-fbui-gray-300 [&_blockquote]:pl-3 [&_blockquote]:text-fbui-gray-600',
            '[&_pre]:my-1 [&_pre]:rounded [&_pre]:bg-fbui-gray-100 [&_pre]:p-2 [&_pre]:font-mono [&_pre]:text-xs',
            '[&_a]:text-fbui-basil-600 [&_a]:underline',
            disabled && 'cursor-not-allowed text-fbui-text-disabled',
            contentClassName
          )}
        />
        {hasFooter && (
          <InputGroupAddon
            align="block-end"
            divided
            className={cn(
              'gap-2',
              footerStart != null ? 'justify-between' : 'justify-end'
            )}
          >
            {footerStart != null && (
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                {footerStart}
              </div>
            )}
            {footer ?? (
              <div className="flex shrink-0 items-center gap-2">
                {onDiscard && (
                  <Button
                    type="button"
                    // Neutral (plain gray hover) — discard shouldn't read as a
                    // brand-positive action (ghost's green) nor shout in red.
                    variant="neutral"
                    size="sm"
                    disabled={disabled}
                    onClick={onDiscard}
                  >
                    {discardLabel}
                  </Button>
                )}
                {onSend && (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={disabled || sendDisabled}
                    onClick={onSend}
                    leftIcon={<PaperPlaneTilt aria-hidden="true" />}
                  >
                    {sendLabel}
                  </Button>
                )}
              </div>
            )}
          </InputGroupAddon>
        )}
      </InputGroup>
    )
  }
)
RichTextEditor.displayName = 'RichTextEditor'
